import React, { Suspense, useCallback, useMemo } from 'react'
import { lazyComponent } from '../../../libs/onUnhandledPromiseRejection'
import { useLocation, useNavigate } from 'react-router-dom'
import { SidebarProvider, useSidebar } from '../../../shared/components/Sidebar'
import { MaterialSidebarLayout } from '../../../shared/components/Sidebar/MaterialSidebarLayout'
import { OrganizationSwitcher } from '../../organization/OrganizationSwitcher'
import { AnySidebarSection, GamerSection, SharedSection, SidebarNavigationItem } from '../../../shared/types/navigation'
import { GAMER_NAVIGATION_ITEMS } from '../constants/navigation'
import { hasIncompleteOnboardingSteps } from './onboardingSteps.config'
import { withFaqLast, withSectionAfter } from '../../../shared/utils/navigationOrderHelpers'
import { getSectionFromPath } from '../../../config/routes/routeConfig'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import {
  DashboardGamerPage,
  OnboardingGamerPage,
  MyProfileGamerPage,
  QAGamerPage,
  UpcomingActivitiesPage,
  AllUpcomingActivitiesPage,
  GameCenterPage,
  PlayGameWithoutIntroductionPage,
  PlayGameWithIntroductionPage,
  ActivityDetailsPage,
  AccountSettingsPage,
  DeeperTeamAnalysisPage,
  DeeperIndividualAnalysisPage,
  AchievementsPage,
  UnseenAchievementsPage,
  AICoachPage,
  SprintCallPage,
  AICoachAriaPage,
  TutorialVideosPage
} from './index'
import { DevToolsAchievementsPage } from '../../shared/pages/DevToolsAchievementsPage'
import { DevToolsShared } from '../../shared/pages/DevToolsShared'
import { BOCleanupPage } from '../../shared/pages/BOCleanupPage'
import { PermissionsInstructionsPage } from '../../../shared/pages/PermissionsInstructionsPage'
import { useFeatureFlags } from '../../../shared/hooks/useFeatureFlags'
import { useIsCoachEnabled } from '../../../shared/hooks/useIsCoachEnabled'
import {
  useGetUpcomingActivitiesListForGamer,
  useGetUpcomingActivityForGamer,
  useLatestFeedbackDataForGamer
} from '../../../apiLayer/queryHooks/queries'
import { useLeaderInsights } from '../../../apiLayer/queryHooks/leader'
import { isLeaderRole } from '../../organization/orgStateHelpers'
// Dev-tool pages are lazy and imported from their own modules — importing them through the
// facilitator pages barrel pulled every facilitator page into the gamer bundle.
const VoiceLottieTest = lazyComponent(() => import('../../facilitator/pages/VoiceLottieTest'), 'VoiceLottieTest')
const BubbleLottieTest = lazyComponent(() => import('../../facilitator/pages/BubbleLottieTest'), 'BubbleLottieTest')
const VoiceAnimationWithAudioOptimized = lazyComponent(
  () => import('../../facilitator/pages/VoiceAnimationWithAudioOptimized'),
  'VoiceAnimationWithAudioOptimized'
)
const RechartsGlassTest = lazyComponent(() => import('../../facilitator/pages/RechartsGlassTest'), 'RechartsGlassTest')
const CommunicationFeelingExamples = lazyComponent(
  () => import('../../facilitator/pages/CommunicationFeelingExamples'),
  'CommunicationFeelingExamples'
)
const StackedChartsTest = lazyComponent(() => import('../../facilitator/pages/StackedChartsTest'), 'StackedChartsTest')
const ComparisonBarChartDevTest = lazyComponent(
  () => import('../../facilitator/pages/ComparisonBarChartDevTest'),
  'ComparisonBarChartDevTest'
)
const TeamHealthHeartDevTest = lazyComponent(
  () => import('../../facilitator/pages/TeamHealthHeartDevTest'),
  'TeamHealthHeartDevTest'
)
const GlintRevealDevTest = lazyComponent(
  () => import('../../facilitator/pages/GlintRevealDevTest'),
  'GlintRevealDevTest'
)
const GenerateReportVisualizationsDev = lazyComponent(
  () => import('../../facilitator/pages/GenerateReportVisualizationsDev'),
  'GenerateReportVisualizationsDev'
)
const FunnelGraphDevTest = lazyComponent(
  () => import('../../facilitator/pages/FunnelGraphDevTest'),
  'FunnelGraphDevTest'
)
const FunnelGraphDevTestV2 = lazyComponent(
  () => import('../../facilitator/pages/FunnelGraphDevTestV2'),
  'FunnelGraphDevTestV2'
)
const FunnelGraphDevTestV3 = lazyComponent(
  () => import('../../facilitator/pages/FunnelGraphDevTestV3'),
  'FunnelGraphDevTestV3'
)
const MyPeoplePage = lazyComponent(() => import('./myPeople/MyPeoplePage'), 'MyPeoplePage')
const PersonalityTraitsVisualizationDevTest = lazyComponent(
  () => import('../../facilitator/pages/PersonalityTraitsVisualizationDevTest'),
  'PersonalityTraitsVisualizationDevTest'
)
import useAppState from '../../../hooks/useAppState'
import { RouteGuard } from '../../../components/RouteGuard'
import { LoadingPage } from '../../../components/LoadingPage'
import LatestSessionFeedbackGamerPageV2 from './LatestSessionFeedbackGamerPageV2'
import { hasAnyLiveActivity, isActivityLive, isAIDebriefOpenForTeam } from '../../shared/helpers/liveActivityHelpers'
import { isWithin5MinutesBeforeStart } from '../helpers/activityHelpers'
import { Spinner } from '../../../interaxDesignSystemComponents'
import dayjs from 'dayjs'
import { useQueryClient } from '@tanstack/react-query'
import { useGamerActivitiesRealtimeSync } from '../hooks/useGamerActivitiesRealtimeSync'
import { useGoToActivityWhenSent } from '../hooks/useGoToActivityWhenSent'
import { useNewFeedbackSectionIds } from '../hooks/useNewFeedbackSectionIds'
import { PlatformTour } from '../platformTour/components/PlatformTour'
import { isPlatformTourAvailable } from '../platformTour/platformTourStops'
import { useActivityStatusTick } from '../../shared/hooks/useActivityStatusTick'
import { useActivityThresholdScheduler } from '../../shared/hooks/useActivityThresholdScheduler'
import { GAMER_ACTIVITY_QUERY_KEYS } from '../../shared/constants/activitySync'

/**
 * Smart redirect: sends users with upcoming/live activities to the Activity Center,
 * and users with no activities to the Dashboard — or to My people for a leader, who has no Dashboard.
 */
const GamerLandingRedirect: React.FC = () => {
  const navigate = useNavigate()
  const { state } = useAppState()
  const homeRoute = isLeaderRole(state.user) ? GamerRoutes.MY_PEOPLE : GamerRoutes.DASHBOARD
  const { data: activitiesResponse, isLoading } = useGetUpcomingActivitiesListForGamer()

  React.useEffect(() => {
    if (isLoading) return
    const activities = activitiesResponse?.data ?? []
    const dest = activities.length > 0 ? GamerRoutes.UPCOMING_ACTIVITIES : homeRoute
    navigate(dest, { replace: true })
  }, [isLoading, activitiesResponse, navigate, homeRoute])

  if (isLoading) return <Spinner />
  return null
}

/**
 * Content renderer component that renders the appropriate page based on active gamer section
 * Uses route config for clean section-to-component mapping
 */
const ContentRenderer: React.FC = () => {
  const { activeSection } = useSidebar()
  const location = useLocation()
  const { isLatestSessionFeedbackEnabled, isAchievementsEnabled } = useFeatureFlags()

  // Get section from URL path using route config
  const sectionFromPath = getSectionFromPath(location.pathname, 'gamer')
  const sectionValue = sectionFromPath || String(activeSection)

  // Map sections to components
  switch (sectionValue) {
    // Gamer-specific sections
    case GamerSection.DASHBOARD_GAMER:
    case 'dashboard-gamer': {
      const normalizedGamerPath = location.pathname.replace(/\/$/, '')
      if (normalizedGamerPath === GamerRoutes.BASE) {
        return <GamerLandingRedirect />
      }
      return <DashboardGamerWithUser />
    }
    case GamerSection.MY_PEOPLE:
    case 'my-people':
      return <MyPeoplePage />
    case GamerSection.LATEST_SESSION_FEEDBACK:
    case 'latest-session-feedback':
      // Only render if feature flag is enabled
      if (!isLatestSessionFeedbackEnabled) {
        return <DashboardGamerWithUser />
      }
      return <LatestSessionFeedbackGamerPageV2 />
    case GamerSection.UPCOMING_ACTIVITIES:
    case 'upcoming-activities':
      return <UpcomingActivitiesPage />
    case GamerSection.ALL_UPCOMING_ACTIVITIES:
    case 'all-upcoming-activities':
      return <AllUpcomingActivitiesPage />
    case GamerSection.ONBOARDING:
    case 'onboarding':
      return <OnboardingGamerPage />
    case GamerSection.ARIA_COACH:
    case 'aria-coach':
      return <AICoachAriaPage />
    case GamerSection.MY_PROFILE:
    case 'my-profile':
      return <MyProfileGamerPage />
    case GamerSection.ACCOUNT_SETTINGS:
    case 'account-settings':
      return <AccountSettingsPage />
    case 'permissions-instructions':
      return <PermissionsInstructionsPage />
    case GamerSection.FAQ:
    case 'faq':
      return <QAGamerPage />
    case 'tutorial-videos':
    case 'tutorial-videos-gamer':
      return <TutorialVideosPage />
    case GamerSection.ACTIVITY_DETAILS:
    case 'activity-details':
      return <ActivityDetailsPage />
    case GamerSection.DEEPER_TEAM_ANALYSIS:
    case 'deeper-team-analysis':
      // Only render if feature flag is enabled
      if (!isLatestSessionFeedbackEnabled) {
        return <DashboardGamerWithUser />
      }
      return <DeeperTeamAnalysisPage />
    case GamerSection.DEEPER_INDIVIDUAL_ANALYSIS:
    case 'deeper-individual-analysis':
      // Only render if feature flag is enabled
      if (!isLatestSessionFeedbackEnabled) {
        return <DashboardGamerWithUser />
      }
      return <DeeperIndividualAnalysisPage />
    case GamerSection.ACHIEVEMENTS:
    case 'achievements':
      if (!isAchievementsEnabled) {
        return <DashboardGamerWithUser />
      }
      return <AchievementsPage />
    case 'unseen-achievements':
      return <UnseenAchievementsPage />
    case 'ai-coach':
      return <AICoachPage />
    case 'sprint-call':
      return <SprintCallPage />

    // Shared sections - Game routes
    case SharedSection.GAME_CENTER:
    case 'game-center':
      return <GameCenterPage />
    case SharedSection.PLAY_GAME_WITH_INTRODUCTION:
    case 'play-game-with-introduction':
      return <PlayGameWithIntroductionPage />
    case SharedSection.PLAY_GAME_WITHOUT_INTRODUCTION:
    case 'play-game-without-introduction':
      return <PlayGameWithoutIntroductionPage />

    // Gamer-specific Dev tools
    case SharedSection.DEV_TOOLS:
    case 'dev-tools':
      return <DevToolsShared />
    case SharedSection.DEV_TOOLS_VOICE_LOTTIE:
    case 'dev-tools-voice-lottie':
      return <VoiceLottieTest />
    case SharedSection.DEV_TOOLS_BUBBLE_LOTTIE:
    case 'dev-tools-bubble-lottie':
      return <BubbleLottieTest />
    case SharedSection.DEV_TOOLS_VOICE_ANIMATION_AUDIO:
    case 'dev-tools-voice-animation-audio':
      return <VoiceAnimationWithAudioOptimized />
    case SharedSection.DEV_TOOLS_RECHARTS_GLASS:
    case 'dev-tools-recharts-glass':
      return <RechartsGlassTest />
    case SharedSection.DEV_TOOLS_COMMUNICATION_FEELING:
    case 'dev-tools-communication-feeling':
      return <CommunicationFeelingExamples />
    case SharedSection.DEV_TOOLS_STACKED_CHARTS:
    case 'dev-tools-stacked-charts':
      return <StackedChartsTest />
    case SharedSection.DEV_TOOLS_COMPARISON_BAR:
    case 'dev-tools-comparison-bar':
      return <ComparisonBarChartDevTest />
    case SharedSection.DEV_TOOLS_TEAM_HEALTH_HEART:
    case 'dev-tools-team-health-heart':
      return <TeamHealthHeartDevTest />
    case SharedSection.DEV_TOOLS_GLINT_REVEAL:
    case 'dev-tools-glint-reveal':
      return <GlintRevealDevTest />
    case SharedSection.DEV_TOOLS_GENERATE_REPORT_VISUALS:
    case 'dev-tools-generate-report-visuals':
      return <GenerateReportVisualizationsDev />
    case SharedSection.DEV_TOOLS_FUNNEL_GRAPH:
    case 'dev-tools-funnel-graph':
      return <FunnelGraphDevTest />
    case SharedSection.DEV_TOOLS_FUNNEL_GRAPH_V2:
    case 'dev-tools-funnel-graph-v2':
      return <FunnelGraphDevTestV2 />
    case SharedSection.DEV_TOOLS_FUNNEL_GRAPH_V3:
    case 'dev-tools-funnel-graph-v3':
      return <FunnelGraphDevTestV3 />
    case SharedSection.DEV_TOOLS_ACHIEVEMENTS:
    case 'dev-tools-achievements':
      return <DevToolsAchievementsPage />
    case SharedSection.DEV_TOOLS_PERSONALITY_TRAITS:
    case 'dev-tools-personality-traits':
      return <PersonalityTraitsVisualizationDevTest />
    case SharedSection.DEV_TOOLS_BO_CLEANUP:
    case 'dev-tools-bo-cleanup':
      return <BOCleanupPage />

    default:
      return <DashboardGamerWithUser />
  }
}

/**
 * DashboardGamer wrapper that provides user name from app state
 */
const DashboardGamerWithUser: React.FC = () => {
  const { state } = useAppState()

  // A leader has no Dashboard, so every fallback to it shows My people instead.
  if (isLeaderRole(state.user)) return <MyPeoplePage />

  const userName = state.user?.displayName || state.user?.email?.split('@')[0] || 'Gamer'
  return <DashboardGamerPage userName={userName} />
}

/**
 * Main Gamer Dashboard Component
 * Provides sidebar navigation and content area for gamer role
 */
const GamerDashboard: React.FC = () => {
  const location = useLocation()
  const { isLatestSessionFeedbackEnabled, isAchievementsEnabled } = useFeatureFlags()
  // Remembered per user, so the coach nav item is there on the first paint rather than arriving
  // a second later and pushing the rest of the menu down.
  const isCoachEnabled = useIsCoachEnabled()

  // Determine if there is a latest session feedback available for the gamer.
  // We refetch on pathname change to avoid spamming requests.
  const latestFeedbackQuery = useLatestFeedbackDataForGamer({
    enabled: isLatestSessionFeedbackEnabled
  })

  React.useEffect(() => {
    if (!isLatestSessionFeedbackEnabled) return
    latestFeedbackQuery.refetch()
    // Intentionally only on path changes (per requirements)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isLatestSessionFeedbackEnabled])

  const hasLatestSessionFeedback = Boolean(latestFeedbackQuery.data?.success && latestFeedbackQuery.data?.data)
  const newSectionIds = useNewFeedbackSectionIds(latestFeedbackQuery.data?.data)
  const isOnLatestSessionFeedbackRoute = location.pathname.replace(/\/$/, '') === GamerRoutes.LATEST_SESSION_FEEDBACK

  const { state } = useAppState()
  const isLeader = isLeaderRole(state.user)
  const leaderInsightsQuery = useLeaderInsights({ enabled: isLeader })
  // Read as on until the server has answered, so the menu does not lose the entry while it loads.
  const areLeaderFeaturesOn = leaderInsightsQuery.data?.data?.featureEnabled ?? true
  const isMyPeopleShown = isLeader && areLeaderFeaturesOn
  const userTeamIds = state.user?.teamIds ?? []
  const { data: activitiesResponse } = useGetUpcomingActivitiesListForGamer()
  const activities = activitiesResponse?.data ?? []

  // Real-time sync: Firestore onSnapshot invalidates cache on any session change
  useGamerActivitiesRealtimeSync()

  // A facilitator can send the whole team to its game or debrief from the Control Tower.
  useGoToActivityWhenSent()

  // Tick: re-evaluates time comparisons every 30s (fallback for the scheduler)
  const tick = useActivityStatusTick()

  // Scheduler: precise setTimeout at each activity's SOON/LIVE/END threshold
  const queryClient = useQueryClient()
  const forceUpdate = useCallback(() => {
    GAMER_ACTIVITY_QUERY_KEYS.forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key] })
    })
  }, [queryClient])
  useActivityThresholdScheduler(activities, forceUpdate)

  const { data: currentActivityResponse } = useGetUpcomingActivityForGamer()
  const isDebriefOpenForTeam = isAIDebriefOpenForTeam(currentActivityResponse?.data)
  const hasLive = useMemo(
    () => isDebriefOpenForTeam || hasAnyLiveActivity(activities, userTeamIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activities, userTeamIds, tick, isDebriefOpenForTeam]
  )
  const liveActivitySectionIds = hasLive ? [GamerSection.UPCOMING_ACTIVITIES] : []

  const hasAnySoon = useMemo(
    () =>
      activities.some(
        (a) =>
          !isActivityLive(a, userTeamIds) &&
          isWithin5MinutesBeforeStart(a) &&
          a.startDate != null &&
          dayjs().isBefore(dayjs(a.startDate))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activities, userTeamIds, tick]
  )
  const soonActivitySectionIds = hasAnySoon ? [GamerSection.UPCOMING_ACTIVITIES] : []

  // Filter navigation items based on feature flags
  const filteredNavigationItems = React.useMemo(() => {
    const filtered = GAMER_NAVIGATION_ITEMS.filter((item) => {
      // Hide Latest Session Feedback if feature flag is disabled
      if (item.id === GamerSection.LATEST_SESSION_FEEDBACK && !isLatestSessionFeedbackEnabled) {
        return false
      }

      // Hide Latest Session Feedback if there is no feedback for the gamer.
      // Keep it visible while the user is already on the page to avoid an active item disappearing.
      if (
        item.id === GamerSection.LATEST_SESSION_FEEDBACK &&
        !hasLatestSessionFeedback &&
        !isOnLatestSessionFeedbackRoute
      ) {
        return false
      }

      // Hide Achievements if feature flag is disabled
      if (item.id === GamerSection.ACHIEVEMENTS && !isAchievementsEnabled) {
        return false
      }

      // No first-game unlock: a participant sets their goals with Aria before they ever play.
      if (item.requiresCoachEnabled && !isCoachEnabled) {
        return false
      }

      // My people belongs to leaders, and only while leader features are on for the organization.
      if (item.requiresLeaderRole && !isMyPeopleShown) {
        return false
      }

      // A leader lands on My people; the gamer Dashboard is not part of their menu.
      if (item.id === GamerSection.DASHBOARD_GAMER && isLeader) {
        return false
      }
      return true
    })
    // Finished onboarding is reference material, not a to-do: it drops below My Profile.
    const ordered = hasIncompleteOnboardingSteps(state.user ?? undefined)
      ? filtered
      : withSectionAfter(filtered, GamerSection.ONBOARDING, GamerSection.MY_PROFILE)
    return withFaqLast(ordered, GamerSection.FAQ)
  }, [
    isLatestSessionFeedbackEnabled,
    isAchievementsEnabled,
    hasLatestSessionFeedback,
    isOnLatestSessionFeedbackRoute,
    isCoachEnabled,
    isLeader,
    isMyPeopleShown,
    state.user
  ])

  // Determine initial active section based on URL using route config
  // When on AchievementsPage, show MY_PROFILE as active since users navigate there from MyProfile
  const sectionFromPath = getSectionFromPath(location.pathname, 'gamer')
  let initialSection = (sectionFromPath as GamerSection) || GamerSection.DASHBOARD_GAMER

  // Override to MY_PROFILE when viewing achievements page
  if (sectionFromPath === 'achievements' || sectionFromPath === GamerSection.ACHIEVEMENTS) {
    initialSection = GamerSection.MY_PROFILE
  }

  return (
    <SidebarProvider
      defaultActiveSection={initialSection as AnySidebarSection}
      navigationItems={filteredNavigationItems as SidebarNavigationItem[]}
    >
      <GamerDashboardContent
        filteredNavigationItems={filteredNavigationItems}
        liveActivitySectionIds={liveActivitySectionIds}
        soonActivitySectionIds={soonActivitySectionIds}
        newSectionIds={newSectionIds}
      />
    </SidebarProvider>
  )
}

/**
 * Inner component that uses sidebar context
 */
const GamerDashboardContent: React.FC<{
  filteredNavigationItems: typeof GAMER_NAVIGATION_ITEMS
  liveActivitySectionIds?: AnySidebarSection[]
  soonActivitySectionIds?: AnySidebarSection[]
  newSectionIds?: AnySidebarSection[]
}> = ({ filteredNavigationItems, liveActivitySectionIds, soonActivitySectionIds, newSectionIds }) => {
  const { state } = useAppState()

  return (
    <MaterialSidebarLayout
      profileTopSlot={<OrganizationSwitcher />}
      navigationItems={filteredNavigationItems as SidebarNavigationItem[]}
      liveActivitySectionIds={liveActivitySectionIds}
      soonActivitySectionIds={soonActivitySectionIds}
      newSectionIds={newSectionIds}
    >
      <RouteGuard>
        <Suspense fallback={<LoadingPage />}>
          <ContentRenderer />
        </Suspense>
      </RouteGuard>
      {isPlatformTourAvailable(state.user) && (
        <PlatformTour navigationItems={filteredNavigationItems as SidebarNavigationItem[]} />
      )}
    </MaterialSidebarLayout>
  )
}

export default GamerDashboard
